/**
 * AtlasMerchantModule — the client half of the merchant counter (the versona
 * review's two-player merchant slice, 2026-09-15).
 *
 * Reads `merchant_service` rows (one per merchant ever approached; the
 * server's sweep keeps them truthful) and, for every merchant at their
 * counter, keys EnvironmentModule's `serving` map (phase) and points its
 * `facing` map at the customer's replicated position (own row, or the AOI's
 * player_state for another player). The body then turns to the customer and
 * nods, and leans in for the handover beat — all drawn from the durable row:
 * a player arriving mid-purchase sees the same beat, and nothing here
 * commits anything (the server's settle is the only economic outcome).
 *
 * `__merchants()` — the counters as the client sees them (probe lever).
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeSystem, Phase } from '../engine-compat.js';
import type { EnvironmentModule } from './EnvironmentModule.js';

const SYNC_EVERY_FRAMES = 15;

export class AtlasMerchantModule implements IModule {
  readonly name = 'AtlasMerchant';
  private frameN = 0;
  /** Versona ids whose facing/serving entries WE own (living events own theirs). */
  private readonly mine = new Set<string>();

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    app.world.addSystem(makeSystem('AtlasMerchantSync', Phase.Animation, 39, () => this.sync()));
    (window as unknown as { __merchants?: () => unknown }).__merchants = () => this.snapshot();
  }

  private sync(): void {
    if ((this.frameN++ % SYNC_EVERY_FRAMES) !== 0) return;
    const conn = atlas.conn;
    if (!conn) return;
    const seen = new Set<string>();
    try {
      for (const s of conn.db.merchantService.iter()) {
        if (s.phase === 'idle') continue;
        const key = String(s.versonaId);
        const p = conn.db.playerState.identity.find(s.customer);
        if (p) this.env.facing.set(key, { x: p.posX, z: p.posZ });
        this.env.serving.set(key, s.phase);
        if (conn.identity && s.customer.isEqual(conn.identity)) this.env.servingMe.add(key); else this.env.servingMe.delete(key);
        seen.add(key);
        this.mine.add(key);
      }
    } catch { /* a table not subscribed yet — nothing to draw */ }
    for (const key of [...this.mine]) {
      if (seen.has(key)) continue;
      this.env.serving.delete(key);
      this.env.servingMe.delete(key);
      this.env.facing.delete(key);
      this.mine.delete(key);
    }
  }

  snapshot(): { counters: Array<{ versonaId: string; phase: string; customerIsMe: boolean; facing: { x: number; z: number } | null }>; waiting: number; holds: number } {
    const conn = atlas.conn;
    const out = { counters: [] as Array<{ versonaId: string; phase: string; customerIsMe: boolean; facing: { x: number; z: number } | null }>, waiting: 0, holds: 0 };
    if (!conn) return out;
    const me = conn.identity;
    for (const s of conn.db.merchantService.iter()) {
      if (s.phase === 'idle') continue;
      out.counters.push({ versonaId: String(s.versonaId), phase: s.phase, customerIsMe: !!me && s.customer.isEqual(me), facing: this.env.facing.get(String(s.versonaId)) ?? null });
    }
    out.waiting = [...conn.db.merchantQueue.iter()].length;
    out.holds = [...conn.db.merchantHold.iter()].length;
    return out;
  }

  dispose(): void {
    for (const key of this.mine) { this.env.serving.delete(key); this.env.servingMe.delete(key); this.env.facing.delete(key); }
    this.mine.clear();
  }
}
